import dayjs from 'dayjs';

import { MinimalUser } from '../../user/types/User';
import {
  OpenClass,
  OpenClassCertificateOffering,
  OpenClassCertificateType,
  OpenClassSession,
  OpenClassStatus,
} from '../types/OpenClass';
import { certificateTypeLabel } from './certificateTypeLabel';

export const formatOpenClassDateRange = (
  openClass: Pick<OpenClass, 'startAt' | 'endAt'>,
): string => {
  const start = dayjs(openClass.startAt);
  const end = dayjs(openClass.endAt);

  if (start.isSame(end, 'day')) {
    return start.format('ddd D MMM YYYY');
  }

  const startFormat = start.isSame(end, 'month') ? 'ddd D' : 'ddd D MMM';
  return `${start.format(startFormat)} – ${end.format('ddd D MMM YYYY')}`;
};

export const isOpenClassPast = (endAt?: string | null): boolean =>
  !!endAt && dayjs(endAt).isBefore(dayjs());

export const isOpenClassAssignable = (
  openClass: Pick<OpenClass, 'status' | 'endAt'>,
): boolean =>
  openClass.status !== OpenClassStatus.Canceled &&
  !isOpenClassPast(openClass.endAt);

export const uniqueSessionLocations = (
  sessions: OpenClassSession[],
): string[] => {
  const locations = sessions
    .map((session) => session.location?.trim())
    .filter((location): location is string => !!location);
  return Array.from(new Set(locations));
};

export const formatOpenClassLocation = (location: string): string => {
  const addressParts = location
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (addressParts.length < 3) return location;

  const city = addressParts[addressParts.length - 2]
    .replace(/^\d{3}\s?\d{2}\s+/, '')
    .trim();

  return city || location;
};

export const uniqueSessionInstructors = (
  sessions: OpenClassSession[],
): MinimalUser[] => {
  const byId = new Map<number | string, MinimalUser>();
  sessions.forEach((session) =>
    session.instructors.forEach((instructor) => {
      const key = instructor.id ?? instructor.email;
      if (!byId.has(key)) {
        byId.set(key, instructor);
      }
    }),
  );
  return Array.from(byId.values());
};

export const sortSessionsByStart = (
  sessions: OpenClassSession[],
): OpenClassSession[] =>
  [...sessions].sort(
    (a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf(),
  );

export const sortOpenClassesByStart = (openClasses: OpenClass[]): OpenClass[] =>
  [...openClasses].sort(
    (a, b) =>
      dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf() || a.id - b.id,
  );

export const openClassOptionLabel = (
  openClass: Pick<OpenClass, 'course' | 'startAt'>,
): string =>
  `${openClass.course.name} (${dayjs(openClass.startAt).format('YYYY-MM-DD')})`;

export const sessionSpansDays = (
  session: Pick<OpenClassSession, 'startAt' | 'endAt'>,
): boolean => !dayjs(session.startAt).isSame(dayjs(session.endAt), 'day');

export const formatSessionTimeRange = (
  session: Pick<OpenClassSession, 'startAt' | 'endAt'>,
): string => {
  const endFormat = sessionSpansDays(session) ? 'YYYY-MM-DD HH:mm' : 'HH:mm';
  return `${dayjs(session.startAt).format('YYYY-MM-DD HH:mm')} – ${dayjs(
    session.endAt,
  ).format(endFormat)}`;
};

export const openClassesById = (
  openClasses: OpenClass[],
): Map<OpenClass['id'], OpenClass> =>
  new Map(openClasses.map((openClass) => [openClass.id, openClass]));

export const offeredCertificateType = (
  openClass: OpenClass | undefined,
  certificateTemplateId: number | undefined,
): OpenClassCertificateType | undefined =>
  openClass?.certificateTemplates.find(({ id }) => id === certificateTemplateId)
    ?.type;

export type OpenClassOption = {
  disabled?: boolean;
  label: string;
  value: number;
};

export const openClassOptions = (
  openClasses: OpenClass[],
  {
    disabledOpenClassIds,
    retainedOpenClassId,
  }: { disabledOpenClassIds?: number[]; retainedOpenClassId?: number } = {},
): OpenClassOption[] => {
  const options: OpenClassOption[] = openClasses
    .filter(isOpenClassAssignable)
    .map((openClass) => ({
      disabled: disabledOpenClassIds?.includes(openClass.id),
      label: openClassOptionLabel(openClass),
      value: openClass.id,
    }));

  const retained = openClasses.find(({ id }) => id === retainedOpenClassId);
  if (retained && !isOpenClassAssignable(retained)) {
    options.push({
      disabled: true,
      label: openClassOptionLabel(retained),
      value: retained.id,
    });
  }
  return options;
};

export const certificateTemplateOptions = (
  templates: Pick<OpenClassCertificateOffering, 'id' | 'type'>[] | undefined,
): { label: string; value: number }[] =>
  (templates ?? []).map((template) => ({
    label: certificateTypeLabel(template.type),
    value: template.id,
  }));
